import React, { createContext, useState, useCallback, useMemo } from 'react';

export const EditModeContext = createContext(null);

export const EditModeProvider = ({ children, onSave: saveCallback, onEditModeChange }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedCells, setEditedCells] = useState(new Map());
  const [selectedRange, setSelectedRange] = useState(null);
  const [activeCell, setActiveCell] = useState(null);

  const toggleEditMode = useCallback(() => {
    const newEditMode = !isEditMode;
    if (newEditMode) {
      // Entering edit mode
      setIsEditMode(true);
      if (onEditModeChange) {
        onEditModeChange(true);
      }
    } else {
      // Exiting edit mode - discard changes
      setEditedCells(new Map());
      setSelectedRange(null);
      setActiveCell(null);
      setIsEditMode(false);
      if (onEditModeChange) {
        onEditModeChange(false);
      }
    }
  }, [isEditMode, onEditModeChange]);

  const updateCell = useCallback((rowId, columnId, value) => {
    setEditedCells(prev => {
      const newMap = new Map(prev);
      
      // If there's a selected range and this cell is part of it,
      // update all cells in the range
      if (selectedRange && columnId === selectedRange.columnId) {
        // Use selectedIds array if available, otherwise fallback to range
        if (selectedRange.selectedIds) {
          selectedRange.selectedIds.forEach(id => {
            const key = `${id}-${columnId}`;
            newMap.set(key, {
              rowId: id,
              columnId,
              value,
              timestamp: Date.now()
            });
          });
        } else {
          for (let currentRow = selectedRange.startRow; currentRow <= selectedRange.endRow; currentRow++) {
            const key = `${currentRow}-${columnId}`;
            newMap.set(key, {
              rowId: currentRow,
              columnId,
              value,
              timestamp: Date.now()
            });
          }
        }
      } else {
        // Single cell update
        const key = `${rowId}-${columnId}`;
        newMap.set(key, {
          rowId,
          columnId,
          value,
          timestamp: Date.now()
        });
      }
      return newMap;
    });
  }, [selectedRange]);

  const getEditedValue = useCallback((rowId, columnId) => {
    const key = `${rowId}-${columnId}`;
    return editedCells.get(key)?.value;
  }, [editedCells]);

  const hasChanges = useMemo(() => editedCells.size > 0, [editedCells]);

  const startCellEdit = useCallback((rowId, columnId, isShiftClick = false) => {
    console.log('startCellEdit:', { rowId, columnId, isShiftClick });
    
    if (isShiftClick) {
      if (!activeCell) {
        console.log('No active cell, setting as start:', rowId);
        setActiveCell({ rowId, columnId });
      } else if (columnId === activeCell.columnId) {
        // Get all row IDs in order from the DOM
        const allRowIds = Array.from(document.querySelectorAll('tr[data-row-id]'))
          .map(tr => tr.getAttribute('data-row-id'));
        
        console.log('All row IDs:', allRowIds);
        
        // Find indices of the clicked rows
        const startIndex = allRowIds.indexOf(activeCell.rowId.toString());
        const endIndex = allRowIds.indexOf(rowId.toString());
        
        console.log('Indices:', { startIndex, endIndex });
        
        if (startIndex !== -1 && endIndex !== -1) {
          // Get the actual range of IDs
          const [minIndex, maxIndex] = [Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)];
          const selectedIds = allRowIds.slice(minIndex, maxIndex + 1);
          
          console.log('Selected IDs:', selectedIds);
          
          setSelectedRange({
            startRow: selectedIds[0],
            endRow: selectedIds[selectedIds.length - 1],
            columnId,
            selectedIds // Store all IDs in the range
          });
        }
      } else {
        console.log('Different column, starting new selection');
        setActiveCell({ rowId, columnId });
        setSelectedRange(null);
      }
    } else {
      console.log('Single cell selection');
      setActiveCell({ rowId, columnId });
      setSelectedRange(null);
    }
  }, [activeCell]);

  const endCellEdit = useCallback(() => {
    setActiveCell(null);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (!activeCell) return;

    const { rowId, columnId } = activeCell;

    // Handle paste event (Ctrl+V or Cmd+V)
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        if (selectedRange) {
          // Get all row IDs in order from the DOM if not already in selectedRange
          const rowsToUpdate = selectedRange.selectedIds || (() => {
            const allRowIds = Array.from(document.querySelectorAll('tr[data-row-id]'))
              .map(tr => tr.getAttribute('data-row-id'));
            const startIndex = allRowIds.indexOf(selectedRange.startRow.toString());
            const endIndex = allRowIds.indexOf(selectedRange.endRow.toString());
            if (startIndex !== -1 && endIndex !== -1) {
              const [minIndex, maxIndex] = [Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)];
              return allRowIds.slice(minIndex, maxIndex + 1);
            }
            return [];
          })();

          // Update each cell in the range
          rowsToUpdate.forEach(rowId => {
            updateCell(rowId, selectedRange.columnId, text);
          });
        } else {
          // Update single cell
          updateCell(rowId, columnId, text);
        }
      }).catch(err => console.error('Failed to read clipboard:', err));
      return;
    }

    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        // Move to next cell
        if (selectedRange) {
          const nextRow = e.shiftKey ? selectedRange.startRow - 1 : selectedRange.endRow + 1;
          setActiveCell({ rowId: nextRow, columnId });
          setSelectedRange(null);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedRange) {
          const nextRow = e.shiftKey ? selectedRange.startRow - 1 : selectedRange.endRow + 1;
          setActiveCell({ rowId: nextRow, columnId });
          setSelectedRange(null);
        }
        break;
      case 'Escape':
        e.preventDefault();
        endCellEdit();
        setSelectedRange(null);
        break;
      default:
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          // If typing starts in a range-selected cell, clear the range
          if (selectedRange) {
            setSelectedRange(null);
          }
        }
        break;
    }
  }, [activeCell, selectedRange, updateCell, endCellEdit]);

  const saveChanges = useCallback(async () => {
    if (!hasChanges) return;

    try {
      const changes = Array.from(editedCells.values()).map(({ rowId, columnId, value }) => ({
        id: rowId,
        field: columnId,
        value
      }));

      if (saveCallback) {
        try {
          const success = await saveCallback(changes);
          if (success) {
            setEditedCells(new Map());
            setIsEditMode(false);
            setSelectedRange(null);
            setActiveCell(null);
          }
          return success;
        } catch (error) {
          console.error('Error in saveCallback:', error);
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Failed to save changes:', error);
      return false;
    }
  }, [hasChanges, editedCells, saveCallback]);

  const cancelChanges = useCallback(() => {
    setEditedCells(new Map());
    setIsEditMode(false);
    setSelectedRange(null);
    setActiveCell(null);
  }, []);

  const value = useMemo(() => ({
    isEditMode,
    toggleEditMode,
    editedCells,
    updateCell,
    getEditedValue,
    hasChanges,
    selectedRange,
    setSelectedRange,
    activeCell,
    startCellEdit,
    endCellEdit,
    handleKeyDown,
    saveChanges,
    cancelChanges
  }), [
    isEditMode,
    toggleEditMode,
    editedCells,
    updateCell,
    getEditedValue,
    hasChanges,
    selectedRange,
    activeCell,
    startCellEdit,
    endCellEdit,
    handleKeyDown,
    saveChanges,
    cancelChanges
  ]);

  return (
    <EditModeContext.Provider value={value}>
      {children}
    </EditModeContext.Provider>
  );
};
